import { createSlice, Dispatch } from '@reduxjs/toolkit';
// components
import { enqueueSnackbar } from 'src/components/snackbar';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';
import uuidv4 from 'src/utils/uuidv4';
import { getDefaultAiModel } from 'src/utils/model';
// global
import { HOST_API } from 'src/config-global';
// types
import { IChatBotState, IChatbotType } from 'src/types/chat';
//
import type { AppDispatch, RootState } from '../store-types';

// ----------------------------------------------------------------------

const initialState: IChatBotState = {
  type: 'text',
  models: [],
  sessions: [],
  sessionsCurrentPage: 0,
  sessionsTotalCount: 0,
  currentSession: null,
  currentSessionId: null,
  currentRequestId: null,
  vectorStore: true,
  isTemporary: false,
  aiModel: null,
  status: {
    creating: false,
    loading: false,
    uploading: false,
    thinking: false,
    empty: false,
    error: null,
  },
  navLoading: false,
};

const slice = createSlice({
  name: 'chatbot',
  initialState,
  reducers: {
    // ON TYPE CHANGE
    setChatbotType(state, action) {
      state.type = action.payload;

      if (action.payload === 'image') {
        state.aiModel = getDefaultAiModel(state.models, 'image');
        state.isTemporary = false;
      } else {
        state.aiModel = getDefaultAiModel(state.models, 'text');
      }
    },

    // ON THINK MORE
    setThinkMore(state, action) {
      const is_thinking = action.payload;

      if (is_thinking && state.models?.length > 0) {
        const default_model =
          state.models?.filter(
            (_) => `${_?.name}`?.toLowerCase()?.includes('xerxes') && _?.status !== 'disable'
          )?.[0] ?? null;
        const select_model = default_model ? { value: default_model.name, name: undefined } : null;
        state.aiModel = select_model;
      } else {
        state.aiModel = getDefaultAiModel(state.models, 'text');
      }
    },

    // GET SESSIONS
    getSessionsSuccess(state, action) {
      const sessions = action.payload?.sessions;
      state.sessionsTotalCount = action.payload?.totalCount ?? 0;

      if (state.sessionsCurrentPage === 0) state.sessions = sessions;
      else state.sessions = state.sessions.concat(sessions);
    },
    // SET SESSION NEXT PAGE
    setSessionsNextPage(state) {
      state.sessionsCurrentPage += 1;
    },
    // GET SESSION
    getSessionSuccess(state, action) {
      const session = action.payload;

      if (session) {
        state.currentSession = session;
        state.currentSessionId = session?.session_id;
        state.type = session?.type || 'text';
      } else {
        state.currentSession = null;
        state.currentSessionId = null;
        state.type = 'text';
        state.aiModel = getDefaultAiModel(state.models, 'text');
      }
    },
    // SET NEW SESSION
    setNewSession(state, action) {
      const new_sessions = state.sessions;
      new_sessions.unshift({
        session_id: action.payload,
        user_id: '',
        title: '',
        type: state.type,
        created_at: null,
        messages: [],
        vector_store_enabled: state.vectorStore,
        is_temporary_chat: false,
        is_pinned: false,
      });
    },
    // ON SESSION UPDATE
    onSessionUpdate(state, action) {
      const { sessionId, title } = action.payload;
      state.sessions = state.sessions?.map((s) =>
        s?.session_id === sessionId ? { ...s, title } : s
      );
    },
    // ON SESSION DELETE
    onSessionDelete(state, action) {
      const sessionId = action.payload;
      state.sessionsTotalCount -= 1;
      state.sessions = state.sessions?.filter((_) => _?.session_id !== sessionId);

      let total_pages = parseInt(String(state.sessionsTotalCount / 15), 10);
      if (total_pages > 0 && state.sessionsTotalCount % 15 !== 0) total_pages += 1;

      if (state.sessionsCurrentPage >= total_pages && state.sessionsCurrentPage > 0)
        state.sessionsCurrentPage = total_pages - 1;
    },

    // GET MODELS
    getModelsSuccess(state, action) {
      const models = action.payload;
      state.models = models;
      state.aiModel = getDefaultAiModel(models, 'text');
    },

    // ON SEND MESSAGE START
    sendMessageStart(state, action) {
      const { session_id, message } = action.payload;

      state.status.loading = true;

      if (session_id) {
        // state.sessions.find((_) => _?.session_id === session_id)?.messages.push(message);
        state.currentSession?.messages.push(message);
      }
    },
    // ON MESSAGE CHUNK
    appendMessageChunk(state, action) {
      const { chunk } = action.payload;

      if (!chunk) return;

      const session = state.currentSession;
      if (!session) return;

      const lastMessage = session.messages?.[session.messages.length - 1];
      if (!lastMessage) return;

      lastMessage.content += chunk;
    },
    // ON SEND MESSAGE
    sendMessageSuccess(state, action) {
      const { session_id, message, model_name } = action.payload;

      if (session_id && typeof message === 'string') {
        state.currentSession?.messages.push({
          content: message,
          files: [],
          image: null,
          message_id: '',
          role: 'assistant',
          model_name,
          created_at: null,
        });
      }
    },
    // SET THINKING
    setThinking(state, action) {
      state.status.thinking = action.payload;
    },
    // ON SEND MESSAGE FAIL
    sendMessageFailure(state) {
      state.status.loading = false;
      state.status.thinking = false;
      state.status.uploading = false;
      // state.status.error = action.payload;
    },
    // ON IMAGE GENERATE FAIL
    imageGenerateFailure(state) {
      state.status.loading = false;
      state.status.uploading = false;
    },

    // ON MESSAGE LAODING
    setMessageLoading(state, action) {
      state.status.loading = action.payload;
    },
    // ON UPLOADING
    setUploading(state, action) {
      state.status.uploading = action.payload;
    },
    // ON CREATING
    setCreating(state, action) {
      state.status.creating = action.payload;
    },

    // ON NAV LAODING
    setNavLoading(state, action) {
      state.navLoading = action.payload;
    },

    // SET CURRENT REQUEST ID
    setCurrentRequestId(state, action) {
      state.currentRequestId = action.payload;
    },

    // SET TEMPORARY CHAT
    setTemporaryChat(state, action) {
      state.isTemporary = action.payload;
    },
    // SET TEMPORARY SESSION
    setTemporarySession(state, action) {
      const new_sessions = state.sessions;
      new_sessions.push({
        session_id: action.payload,
        user_id: '',
        title: '',
        type: 'text',
        created_at: null,
        messages: [],
        vector_store_enabled: state.vectorStore,
        is_temporary_chat: true,
      });
    },

    // SET VECTOR STORE
    setVectorStore(state, action) {
      state.vectorStore = action.payload;
    },

    // SET AI MODEL
    setAIModel(state, action) {
      state.aiModel = action.payload;
    },

    // RESET ACTIVE SESSION
    resetActiveSession(state) {
      state.currentSession = null;
      state.currentSessionId = null;
    },
    // RESET SESSIONS CURRENT PAGE
    resetSessionsCurrentPage(state) {
      state.sessionsCurrentPage = 0;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  setChatbotType,
  setThinkMore,
  resetActiveSession,
  resetSessionsCurrentPage,
  setSessionsNextPage,
  setTemporaryChat,
  setVectorStore,
  setAIModel,
  sendMessageStart,
  setTemporarySession,
} = slice.actions;

// ----------------------------------------------------------------------

export function getModels() {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await axios.get(API_ENDPOINTS.models, {
        params: {
          skip: 0,
          limit: 50,
        },
      });
      dispatch(slice.actions.getModelsSuccess(response.data));
    } catch (error) {
      console.error(error);
    }
  };
}

// ----------------------------------------------------------------------

export function getSessions() {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(slice.actions.setNavLoading(true));
      const state = getState();
      const currentPage = state.chatbot.sessionsCurrentPage ?? 0;
      if (currentPage === 0) await new Promise((resolve) => setTimeout(resolve, 250));
      const response = await axios.get(API_ENDPOINTS.session.root, {
        params: {
          skip: currentPage * 15,
          limit: 15,
        },
      });

      dispatch(slice.actions.getSessionsSuccess(response.data));
      dispatch(slice.actions.setNavLoading(false));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.setNavLoading(false));
    }
  };
}

// ----------------------------------------------------------------------

let sessionPollTimeout: ReturnType<typeof setTimeout> | null = null;
let activeSessionId: string | null = null;

export function getSession(sessionId: string, timeout?: boolean) {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    if (timeout && !state.chatbot.currentSessionId) {
      activeSessionId = null;
      if (sessionPollTimeout) {
        clearTimeout(sessionPollTimeout);
        sessionPollTimeout = null;
      }
      return;
    }
    if (activeSessionId !== sessionId) {
      activeSessionId = sessionId;
      if (sessionPollTimeout) {
        clearTimeout(sessionPollTimeout);
        sessionPollTimeout = null;
      }
    }

    try {
      const response = await axios.get(`${API_ENDPOINTS.session.root}${sessionId}`);
      dispatch(slice.actions.getSessionSuccess(response.data));
      const session = response.data;
      const lastMessage = session?.messages?.at(-1);

      const last_model = lastMessage?.model_name ?? null;
      const last_ok =
        state.chatbot.models
          ?.filter((_: any) => _?.name === last_model)?.[0]
          ?.status?.toLowerCase() === 'active';

      if (session?.type === 'image') {
        dispatch(
          slice.actions.setAIModel(
            last_ok
              ? { value: last_model, name: undefined }
              : getDefaultAiModel(state.chatbot.models, 'image')
          )
        );
      } else if (session?.type === 'text') {
        dispatch(
          slice.actions.setAIModel(
            last_ok
              ? { value: last_model, name: undefined }
              : getDefaultAiModel(state.chatbot.models, 'text')
          )
        );
      }

      const status = lastMessage?.image?.status;
      const shouldPoll =
        status === 'WAITING' ||
        status === 'RUNNING' ||
        (status === 'COMPLETED' && !lastMessage?.image?.generations?.[0]?.url);

      if (sessionPollTimeout) {
        clearTimeout(sessionPollTimeout);
        sessionPollTimeout = null;
      }

      if (shouldPoll) {
        sessionPollTimeout = setTimeout(() => {
          if (activeSessionId === sessionId) {
            dispatch(getSession(sessionId, true));
          }
        }, 14000);
      }
    } catch (error) {
      console.error(error);
    }
  };
}

// ----------------------------------------------------------------------

export function updateSession(sessionId: string, title: string) {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(slice.actions.onSessionUpdate({ sessionId, title }));
      await axios.patch(API_ENDPOINTS.session.update?.replace('{session_id}', sessionId), {
        title,
      });
    } catch (error) {
      console.error(error);
    }
  };
}

export function pinSession(sessionId: string, pinned: boolean) {
  return async (dispatch: AppDispatch) => {
    try {
      await axios.patch(API_ENDPOINTS.session.pin?.replace('{session_id}', sessionId), null, {
        params: {
          session_id: sessionId,
          pinned: Boolean(pinned),
        },
      });
      // dispatch(slice.actions.onSessionDelete(sessionId));
      dispatch(resetSessionsCurrentPage());
      await dispatch(getSessions());
    } catch (error) {
      console.error(error);
    }
  };
}

export function deleteSession(sessionId: string) {
  return async (dispatch: AppDispatch) => {
    try {
      await axios.delete(`${API_ENDPOINTS.session.root}${sessionId}`);
      // dispatch(slice.actions.onSessionDelete(sessionId));
      dispatch(resetSessionsCurrentPage());
      await dispatch(getSessions());
    } catch (error) {
      console.error(error);
    }
  };
}

// ----------------------------------------------------------------------

export function stopStream() {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const state = getState();
      const requestId = state.chatbot.currentRequestId;
      await axios.post(API_ENDPOINTS.session.stop + requestId);
      dispatch(slice.actions.setCurrentRequestId(null));
      dispatch(slice.actions.setThinking(false));
      dispatch(slice.actions.setMessageLoading(false));
    } catch (error) {
      console.error(error);
    }
  };
}

// ----------------------------------------------------------------------

export function generateImage(session_id: string, prompt: string, files: File[]) {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    try {
      const model = state.chatbot.aiModel?.value ?? '';
      const has_file = files?.length > 0;
      let file_response = null;

      dispatch(
        slice.actions.sendMessageSuccess({
          session_id,
          message: '',
          model_name: model,
        })
      );

      if (has_file) {
        dispatch(slice.actions.setUploading(true));
        const formData = new FormData();
        formData.append('session_id', session_id);
        formData.append('file', files?.[0]);
        file_response = await axios.post(API_ENDPOINTS.upload_image, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        dispatch(slice.actions.setUploading(false));
      }

      await axios.post(API_ENDPOINTS.session.image_generation, {
        session_id,
        model_name: model,
        prompt,
        image_input: file_response?.data?.metis_url,
      });

      activeSessionId = session_id;
      await dispatch(getSession(session_id));
      await dispatch(getSessions());
      dispatch(slice.actions.setMessageLoading(false));
    } catch (error) {
      console.error(error);
      await dispatch(getSessions());
      dispatch(slice.actions.imageGenerateFailure());
    }
  };
}

export function sendMessage(
  sessionId: string,
  body: string,
  files: File[],
  store: boolean,
  fetchSessions = false
) {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    try {
      const model = state.chatbot.aiModel?.value ?? '';
      const has_file = files?.length > 0;
      let file_response = null;

      dispatch(
        slice.actions.sendMessageSuccess({
          session_id: sessionId,
          message: '',
          model_name: model,
        })
      );

      if (has_file) {
        dispatch(slice.actions.setUploading(true));
        const formData = new FormData();
        formData.append('file', files?.[0]);
        file_response = await axios.post(API_ENDPOINTS.upload_file, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        dispatch(slice.actions.setUploading(false));
      }

      const response = await fetch(`${HOST_API}${API_ENDPOINTS.session.ask}?model_name=${model}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage?.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          question: body,
          files: has_file
            ? [
                {
                  path: file_response?.data?.path,
                  type: files?.[0]?.type,
                },
              ]
            : [],
          vector_store_enabled: store,
        }),
      });
      // !response.ok
      if (response.status < 200 || response.status >= 400) {
        response
          .json()
          .then((resp) => {
            enqueueSnackbar(resp?.detail || 'خطایی رخ داده است', {
              variant: 'error',
            });
            throw new Error(resp?.detail || 'Something went wrong');
          })
          .catch((error) => {
            console.error(error);
          });
      }

      if (!response.body) {
        console.error('No response body');
        enqueueSnackbar('خطایی رخ داده است', {
          variant: 'error',
        });
        throw new Error('No response body');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');

      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        // console.log('Received chunk:', chunk);
        let is_request_id = false;

        if (chunk.includes(`data: {"event": "start",`)) {
          is_request_id = true;
          const request_id = chunk.split(`"request_id":`)?.[1]?.split(`"`)?.[1]?.trim();
          // console.log(request_id);
          dispatch(slice.actions.setCurrentRequestId(request_id));
        }

        if (chunk.includes(`<think>`)) dispatch(slice.actions.setThinking(true));
        else if (chunk.includes(`</think>`)) dispatch(slice.actions.setThinking(false));

        if (!is_request_id) dispatch(slice.actions.appendMessageChunk({ chunk }));
      }

      if (!state.chatbot.isTemporary && fetchSessions) await dispatch(getSessions());
      dispatch(slice.actions.setMessageLoading(false));
      dispatch(slice.actions.setThinking(false));
      dispatch(slice.actions.setCurrentRequestId(null));
    } catch (error) {
      console.error('Stream error:', error);
      if (!state.chatbot.isTemporary && fetchSessions) await dispatch(getSessions());
      dispatch(slice.actions.sendMessageFailure());
      dispatch(slice.actions.setCurrentRequestId(null));
      throw new Error(error);
    }
  };
}

// ----------------------------------------------------------------------

export function createNewSession(
  body: string,
  files: File[],
  store: boolean,
  isTemporary: boolean,
  type: IChatbotType
) {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(slice.actions.setCreating(true));
      const has_file = files?.length > 0;

      const data = {
        vector_store_enabled: store,
        is_temporary_chat: isTemporary,
        type,
      };
      const response = await axios.post(API_ENDPOINTS.session.root, data);
      const session_id = response.data?.session_id;

      dispatch(slice.actions.setCreating(false));

      dispatch(
        slice.actions.getSessionSuccess({
          session_id,
          messages: [],
          is_pinned: false,
          type,
        })
      );

      if (!isTemporary) {
        dispatch(resetSessionsCurrentPage());
        // await dispatch(getSessions());
        dispatch(slice.actions.setNewSession(session_id));
      } else dispatch(setTemporarySession(session_id));

      dispatch(
        sendMessageStart({
          session_id: session_id ?? '',
          message: {
            message_id: uuidv4(),
            role: 'user',
            content: body,
            type,
            files: has_file
              ? [
                  {
                    path: '',
                    type: files?.[0]?.type,
                  },
                ]
              : [],
          },
        })
      );

      if (type === 'text') await dispatch(sendMessage(session_id, body, files, store, true));
      else if (type === 'image') await dispatch(generateImage(session_id, body, files));
    } catch (error) {
      dispatch(slice.actions.setCreating(false));
      console.error(error);
    }
  };
}
